import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip
} from '@mui/material';

// Get color for segment - consistent with emails page
const getSegmentColor = (segment_name: string) => {
  switch (segment_name?.toLowerCase()) {
    case 'newsletter':
      return '#3b82f6'; // blue
    case 'luxury':
      return '#9333ea'; // purple
    case 'romance':
      return '#ec4899'; // pink
    case 'family':
      return '#22c55e'; // green
    case 'ultralux':
      return '#f59e0b'; // amber
    case 'unsubscribed':
      return '#9ca3af'; // light grey
    default:
      return '#3174ad'; // default blue
  }
};

interface EmailPreviewModalProps {
  open: boolean;
  onClose: () => void;
  email: {
    name: string;
    subject: string;
    html: string | null;
    scheduled_date: string | null;
    agency_name: string;
    segment_name?: string | null;
    existing_tags: Array<{ id: number; name: string }>;
  } | null;
  loading?: boolean;
}

const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({ open, onClose, email, loading = false }) => {
  if (!email) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box>
          <Typography variant="h6" gutterBottom>
            {email.name || 'Untitled Email'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {email.subject || 'No Subject'}
          </Typography>
          <Box display="flex" gap={1} mt={1} flexWrap="wrap">
            {email.segment_name && (
              <Chip 
                label={email.segment_name.charAt(0).toUpperCase() + email.segment_name.slice(1)} 
                size="small" 
                sx={{
                  backgroundColor: getSegmentColor(email.segment_name),
                  color: 'white',
                  height: '24px',
                  minWidth: '80px',
                  '& .MuiChip-label': {
                    px: 1,
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'visible'
                  }
                }}
              />
            )}
            {email.scheduled_date && (
              <Chip 
                label={`${new Date(email.scheduled_date).getTime() > Date.now() ? 'Scheduled:' : 'Sent:'} ${new Date(email.scheduled_date).toLocaleDateString()}`} 
                size="small" 
                variant="outlined" 
              />
            )}
            {email.agency_name && (
              <Chip 
                label={email.agency_name} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
            )}
            {(email.existing_tags || []).map(tag => (
              <Chip 
                key={tag.id}
                label={tag.name} 
                size="small" 
                color="secondary" 
                variant="outlined" 
              />
            ))}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography>Loading email content...</Typography>
          </Box>
        ) : email.html ? (
          <Box 
            dangerouslySetInnerHTML={{ __html: email.html }} 
            sx={{ 
              '& img': { maxWidth: '100%', height: 'auto' },
              '& table': { maxWidth: '100%' }
            }}
          />
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography color="text.secondary">No HTML content available</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailPreviewModal;
